import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Legal = () => {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      <div className="legal-container">
        <button onClick={() => navigate('/')} className="back-to-home">
          <ArrowLeft size={20} />
          <span>Back to Home</span>
        </button>

        <div className="legal-header">
          <div className="logo-text">WestBay</div>
          <h1 className="legal-title">Legal & Intellectual Property</h1>
          <p className="legal-subtitle">Protecting innovation in cloud security and AI</p>
        </div>

        <div className="legal-content">
          
          {/* Trademark Notice */}
          <section className="legal-section">
            <h2 className="section-title">Trademark Notice</h2>
            <div className="section-content">
              <p className="body-large">
                <strong>Poser AI™</strong> is a trademark of Westbay Group LLC, filed with the United States Patent 
                and Trademark Office (USPTO) on <strong>November 15, 2025</strong>.
              </p>
              <p>
                The Poser AI™ name, logo, and associated branding elements are proprietary marks that identify 
                our AI-powered cloud solutions architect platform. Unauthorized use of this trademark or any 
                confusingly similar marks is strictly prohibited.
              </p>
              <div className="trademark-info">
                <h3>Proper Usage</h3>
                <ul>
                  <li>Always use the ™ symbol when referring to Poser AI in written materials</li>
                  <li>Poser AI™ should be used as an adjective followed by a noun (e.g., "Poser AI™ platform")</li>
                  <li>Do not alter, modify, or create derivatives of the Poser AI™ mark</li>
                  <li>The trademark should always appear in a distinctive format from surrounding text</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Patent Pending */}
          <section className="legal-section">
            <h2 className="section-title">Patent Pending</h2>
            <div className="section-content">
              <p className="body-large">
                The technology, processes, and systems underlying <strong>Poser AI™</strong> are subject to 
                patent protection. A patent application was filed on <strong>November 15, 2025</strong>, 
                covering our innovative approach to AI-powered cloud architecture consultation and security guidance.
              </p>
              <div className="patent-info">
                <h3>Protected Technology</h3>
                <p>Our patent-pending technology includes, but is not limited to:</p>
                <ul>
                  <li>AI-driven cloud architecture analysis and recommendation systems</li>
                  <li>Real-time security assessment and vulnerability detection methodologies</li>
                  <li>Human-in-the-loop AI architecture consultation frameworks</li>
                  <li>Integration of large language models with cloud security best practices</li>
                  <li>Proprietary training and fine-tuning processes for cloud-focused AI models</li>
                </ul>
                <p className="patent-warning">
                  <strong>Notice:</strong> This technology is protected under U.S. patent law. Any unauthorized 
                  use, reproduction, or distribution of this technology may result in civil and criminal penalties, 
                  including monetary damages and injunctive relief.
                </p>
              </div>
            </div>
          </section>

          {/* Intellectual Property Policy */}
          <section className="legal-section">
            <h2 className="section-title">Intellectual Property Policy</h2>
            <div className="section-content">
              <h3>Ownership</h3>
              <p>
                All intellectual property rights in and to the Poser AI™ platform, including but not limited 
                to software, algorithms, designs, documentation, and content, are owned exclusively by 
                Westbay Group LLC or its licensors.
              </p>

              <h3>License to Use</h3>
              <p>
                By accessing and using the Poser AI™ platform, you are granted a limited, non-exclusive, 
                non-transferable, revocable license to use the service for your personal or internal business 
                purposes only. This license does not grant you any rights to:
              </p>
              <ul>
                <li>Copy, modify, or create derivative works of the platform</li>
                <li>Reverse engineer, decompile, or disassemble any part of the system</li>
                <li>Use the platform to develop competing products or services</li>
                <li>Extract or collect data through automated means (scraping, bots, etc.)</li>
                <li>Remove or alter any proprietary notices or labels</li>
              </ul>

              <h3>User-Generated Content</h3>
              <p>
                You retain ownership of any content, data, or queries you submit to Poser AI™. However, 
                by using the platform, you grant Westbay Group LLC a worldwide, royalty-free license to 
                use anonymized and aggregated versions of your interactions to improve our services, 
                train our models, and develop new features.
              </p>

              <h3>Third-Party Rights</h3>
              <p>
                Poser AI™ integrates with third-party services, including AWS Bedrock and Anthropic Claude 
                models. Use of these services is subject to their respective terms and conditions. We do not 
                claim ownership of third-party intellectual property.
              </p>
            </div>
          </section>

          {/* Terms of Use */}
          <section className="legal-section">
            <h2 className="section-title">Terms of Use</h2>
            <div className="section-content">
              <p className="effective-date">Effective Date: November 15, 2025</p>

              <h3>1. Acceptance of Terms</h3>
              <p>
                By accessing or using the Poser AI™ platform and Westbay Group LLC services, you agree to 
                be bound by these Terms of Use. If you do not agree to these terms, do not use our services.
              </p>

              <h3>2. Service Description</h3>
              <p>
                Poser AI™ is an AI-powered cloud solutions architect that provides guidance on cloud security, 
                DevOps practices, and infrastructure architecture. The service is provided "as is" and is 
                intended for informational purposes only.
              </p>

              <h3>3. User Responsibilities</h3>
              <p>You agree to:</p>
              <ul>
                <li>Provide accurate and complete information during registration</li>
                <li>Maintain the confidentiality of your account credentials</li>
                <li>Use the service only for lawful purposes</li>
                <li>Not attempt to gain unauthorized access to our systems</li>
                <li>Not use the service to harm, threaten, or harass others</li>
                <li>Not violate any applicable local, state, national, or international law</li>
              </ul>

              <h3>4. Limitation of Liability</h3>
              <p>
                <strong>IMPORTANT:</strong> Poser AI™ provides AI-generated guidance that should be reviewed 
                by qualified professionals before implementation. Westbay Group LLC is not liable for any 
                damages arising from your use of our platform, including but not limited to direct, indirect, 
                incidental, or consequential damages.
              </p>
              <p>
                We make no warranties regarding the accuracy, completeness, or reliability of AI-generated 
                content. Always consult with qualified cloud architects and security professionals before 
                making critical infrastructure decisions.
              </p>

              <h3>5. Privacy & Data Security</h3>
              <p>
                We take your privacy seriously. Your data is encrypted in transit and at rest. We do not 
                sell your personal information to third parties. For detailed information, please refer to 
                our Privacy Policy (coming soon).
              </p>

              <h3>6. Account Termination</h3>
              <p>
                We reserve the right to suspend or terminate your account at any time for violation of these 
                terms, suspicious activity, or for any other reason at our sole discretion. You may also 
                request account deletion by contacting us at hello@westbay.io.
              </p>

              <h3>7. Modifications to Terms</h3>
              <p>
                We reserve the right to modify these Terms of Use at any time. Changes will be effective 
                immediately upon posting. Your continued use of the service after changes constitutes 
                acceptance of the modified terms.
              </p>

              <h3>8. Dispute Resolution</h3>
              <p>
                Any disputes arising from these terms or your use of Poser AI™ shall be governed by the 
                laws of the State of Florida, without regard to conflict of law principles. You agree to 
                submit to the exclusive jurisdiction of courts located in Tampa, Florida.
              </p>

              <h3>9. Contact Information</h3>
              <p>
                For questions about these terms, trademark usage, or intellectual property matters, 
                please contact us:
              </p>
              <div className="contact-info">
                <p><strong>Westbay Group LLC</strong></p>
                <p>3730 Midtown Dr #1638</p>
                <p>Tampa, FL 33607</p>
                <p>Email: hello@westbay.io</p>
              </div>
            </div>
          </section>

          {/* DMCA Policy */}
          <section className="legal-section">
            <h2 className="section-title">DMCA & Copyright Policy</h2>
            <div className="section-content">
              <p>
                Westbay Group LLC respects the intellectual property rights of others. If you believe that 
                your copyrighted work has been copied in a way that constitutes copyright infringement, 
                please provide our designated agent with the following information:
              </p>
              <ul>
                <li>Identification of the copyrighted work claimed to have been infringed</li>
                <li>Identification of the material that is claimed to be infringing</li>
                <li>Your contact information (address, phone number, email)</li>
                <li>A statement of good faith belief that use is not authorized</li>
                <li>A statement that the information is accurate, under penalty of perjury</li>
                <li>Physical or electronic signature of the copyright owner or authorized agent</li>
              </ul>
              <p>
                Send DMCA notices to: <strong>hello@westbay.io</strong>
              </p>
            </div>
          </section>

        </div>

        <div className="legal-footer">
          <p>Last Updated: November 15, 2025</p>
          <p>© 2026 Westbay Group LLC. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Legal;
